import {
  RequestType,
  WorkflowRequestFragment,
  WorkflowRequestStatus,
  WorkflowRequestsQueryQuery,
  WorkflowRequestsWorkflowRequestFragment,
} from 'gql/graphql';
import { UseQueryResult } from '@tanstack/react-query';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  MenuItemType,
  RequestOptionsMenu,
} from 'components/Requests/components/RequestOptionsMenu/RequestOptionsMenu';
import { RequestsTable } from './Components/RequestsTable/RequestsTable';
import {
  AssignedToCell,
  DueDateCell,
  PercentCompleteCell,
  RequestTitleCell,
  UpdatedAtCell,
} from './Components/TableCells';

type OpenRequestsTableProps = {
  query: UseQueryResult<WorkflowRequestsQueryQuery>;
  onRowClick: (id: string, type: RequestType) => void;
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
};

const columns: GridColDef<WorkflowRequestsWorkflowRequestFragment>[] = [
  {
    field: 'title',
    headerName: 'Name',
    flex: 1.5,
    sortable: false,
    renderCell: (params) => <RequestTitleCell params={params} labelIsType />,
  },
  {
    field: '_assignedContact',
    headerName: 'Assigned To',
    flex: 1,
    sortable: false,
    renderCell: (params) => <AssignedToCell params={params} />,
  },
  {
    field: '_assignedAccount',
    headerName: 'Account',
    flex: 1,
    sortable: false,
    valueGetter: (_value, row) => `${row.assignedAccount?.name || ''}`,
  },
  {
    field: '_owner',
    headerName: 'Owner',
    flex: 1,
    sortable: false,
    valueGetter: (_value, row) =>
      row?.owner?.employee &&
      `${row.owner?.employee.firstName} ${row.owner?.employee.lastName}`,
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    flex: 1,
    sortable: true,
    renderCell: (params) => (
      <DueDateCell params={params} highlightPastDue showReminder />
    ),
  },
  {
    field: 'percentComplete',
    headerName: 'Progress',
    flex: 1,
    sortable: false,
    renderCell: (params) => <PercentCompleteCell params={params} />,
  },
  {
    field: 'updatedAt',
    headerName: 'Last Activity',
    flex: 1,
    sortable: true,
    renderCell: (params) => <UpdatedAtCell params={params} />,
  },
  {
    field: '_menuOptions',
    headerName: ' ',
    width: 100,
    sortable: false,
    renderCell: (params) => {
      const menuItems: MenuItemType[] = ['archive', 'recall', 'changeDueDate'];

      if (params.row.workflow.type !== 'TaxDeliveryWorkflow')
        menuItems.push('zip');

      return (
        <RequestOptionsMenu
          workflowRequest={params.row as WorkflowRequestFragment}
          menuItems={menuItems}
        />
      );
    },
  },
];

export const OpenRequestsTable = ({
  query,
  onRowClick,
  paginationModel,
  setPaginationModel,
}: OpenRequestsTableProps) => (
  <RequestsTable
    query={query}
    onRowClick={onRowClick}
    paginationModel={paginationModel}
    setPaginationModel={setPaginationModel}
    tableName="Open Requests"
    columns={columns}
    requestKey={WorkflowRequestStatus.Requested}
  />
);
